import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import iconCloseLarge from "~/assets/icon-close-large.svg";

import { cn } from "~/lib/utils";
import Image from "~/components/atoms/Image";
import { cva, VariantProps } from "class-variance-authority";

const Modal = DialogPrimitive.Root;

const ModalTrigger = DialogPrimitive.Trigger;

const ModalPortal = DialogPrimitive.Portal;

const ModalClose = DialogPrimitive.Close;

const ModalOverlay = React.forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn("fixed inset-0 z-50 grid place-items-center bg-gray-90/[0.4]", className)}
        {...props}
    />
));
ModalOverlay.displayName = DialogPrimitive.Overlay.displayName;

const ModalContent = React.forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <ModalPortal>
        <ModalOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                "fixed left-[50%] top-[50%] z-50 flex translate-x-[-50%] translate-y-[-50%] flex-col overflow-y-auto rounded-none bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:max-h-[90dvh] sm:max-w-[90dvw] sm:rounded-2xl",
                className,
            )}
            {...props}
        >
            {children}
        </DialogPrimitive.Content>
    </ModalPortal>
));
ModalContent.displayName = DialogPrimitive.Content.displayName;

interface CommonModalSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    withBorders?: boolean;
    withShadows?: boolean;
}

const ModalHeader = ({
    className,
    withClose = true,
    withBorders = true,
    withShadows = true,
    ...props
}: CommonModalSectionProps & { withClose?: boolean }) => {
    const { children, ...rest } = props;
    return (
        <div
            className={cn(
                "z-1 sticky top-0 flex flex-col space-y-1.5 bg-white p-[25px]",
                withShadows ? "shadow-[0px_2px_4px_0px_rgba(157,157,157,0.25)]" : "",
                withBorders ? "border-b border-blue-20" : "",
                className,
            )}
            {...rest}
        >
            {children}
            {withClose && (
                <DialogPrimitive.Close className="absolute right-[25px] top-[22px] rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none">
                    <Image src={iconCloseLarge} alt="Close Dialog" />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            )}
        </div>
    );
};
ModalHeader.displayName = "DialogHeader";

const ModalFooter = ({
    className,
    withBorders = true,
    withShadows = true,
    ...props
}: CommonModalSectionProps) => (
    <div
        className={cn(
            "z-1 sticky bottom-0 mt-auto flex items-center justify-between bg-white p-[25px] sm:space-x-2",
            withShadows ? "shadow-[0px_-2px_4px_0px_rgba(157,157,157,0.25)]" : "",
            withBorders ? "border-t border-blue-20" : "",
            className,
        )}
        {...props}
    />
);
ModalFooter.displayName = "DialogFooter";

const titleVariants = cva("font-condensed", {
    variants: {
        heading: {
            modal: [
                "font-subheader-1 sm:font-header-small mr-[27px] text-center text-gray-80 sm:text-left sm:uppercase sm:text-blue-80",
            ],
            confirmation: ["font-subheader-1 overflow-auto text-wrap text-gray-80"],
        },
    },
    defaultVariants: {
        heading: "modal",
    },
});

type ModalTitleProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> &
    VariantProps<typeof titleVariants>;

const ModalTitle = React.forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Title>,
    ModalTitleProps
>(({ className, heading, ...props }, ref) => {
    const classNames = cn(titleVariants({ heading }), className);

    return <DialogPrimitive.Title ref={ref} className={classNames} {...props} />;
});
ModalTitle.displayName = DialogPrimitive.Title.displayName;

const ModalDescription = React.forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn("sr-only text-sm", className)}
        {...props}
    />
));
ModalDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Modal,
    ModalPortal,
    ModalOverlay,
    ModalClose,
    ModalTrigger,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalTitle,
    ModalDescription,
};
